import * as Sentry from '@sentry/react';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { MessageFeedback } from '../../../schemas/agent-conversation.schema';
import { submitMessageFeedback } from '../../../services/queries/AgentQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { MessageData } from '../components/Message';
import { ChatMessagesAtom } from './chatActionHooks';

interface UseMessageFeedbackReturn {
  submitFeedback: (messageId: number, feedback: MessageFeedback) => void;
  isPending: boolean;
}

/**
 * Hook for submitting message feedback with optimistic updates.
 *
 * Handles the feedback submission lifecycle:
 * 1. Optimistically updates the UI immediately
 * 2. Submits to the API
 * 3. Rolls back on error and shows a toast
 */
export function useMessageFeedback(): UseMessageFeedbackReturn {
  const { pushErrorToast } = useToastMessageState();
  const [isPending, setIsPending] = useState(false);

  const submitFeedback = useAtomCallback(
    useCallback(
      async (get, set, messageId: number, feedback: MessageFeedback) => {
        const previousMessages = get(ChatMessagesAtom);

        // Find the message to update
        const messageIndex = previousMessages.findIndex((m) => m.id === messageId);
        if (messageIndex === -1) {
          const errorMsg = `useMessageFeedback: Message with id ${messageId} not found in state`;
          console.warn(errorMsg);
          Sentry.logger.warn(errorMsg);
          return;
        }

        const previousFeedback = previousMessages[messageIndex].feedback;

        // Optimistically update the UI
        const updatedMessages: MessageData[] = previousMessages.map((m) =>
          m.id === messageId ? { ...m, feedback } : m
        );
        set(ChatMessagesAtom, updatedMessages);

        setIsPending(true);

        try {
          await submitMessageFeedback(messageId, feedback);
        } catch (error) {
          const currentMessages = get(ChatMessagesAtom);
          set(
            ChatMessagesAtom,
            currentMessages.map((m) => (m.id === messageId ? { ...m, feedback: previousFeedback } : m))
          );
          pushErrorToast({
            message: getErrorMessage(error, 'Failed to submit feedback'),
          });
        } finally {
          setIsPending(false);
        }
      },
      [pushErrorToast]
    )
  );

  return {
    submitFeedback,
    isPending,
  };
}
